import qs, { stringify } from "query-string";

import { AxiosInstance } from "@bps/http-client";
import {
  AddDeploymentRingFieldDeviceRequest,
  BulkActionResponse,
  BulkAssignDeploymentRingRequest,
  BulkAssignTenantDeploymentRingRequest,
  BulkAuthActionRequest,
  BulkCommandDispatchRequest,
  CommandResponse,
  CustomRJSFSchema,
  DeploymentDetail,
  DeploymentDetailDto,
  DeploymentRing,
  DeploymentRingCreateRequest,
  DeploymentRingFieldDevice,
  DeploymentRingFieldDeviceDetail,
  DeploymentRingUpdateRequest,
  DeviceAdvancedSearchArgs,
  DeviceCommand,
  DeviceConfigurationDiff,
  DeviceDetail,
  DeviceDetailDto,
  DeviceSummary,
  FieldConfigurationVersion,
  FieldDevice,
  FieldDeviceAuthAction,
  FieldDeviceAuthActionDto,
  FieldDeviceAuthActionRequest,
  FieldDeviceCacheType,
  FieldDeviceClaim,
  FieldDeviceClaimDto,
  FieldDeviceConfiguration,
  FieldDeviceDetails,
  FieldDeviceDetailsDto,
  FieldDeviceDto,
  FieldDeviceFilterArgs,
  FieldDeviceIdentifier,
  FieldDeviceIdentifierDto,
  FieldDeviceProperty,
  FieldDevicePropertyDto,
  FieldDeviceReportedConfig,
  FieldDeviceReportedConfigDto,
  FieldDeviceSetting,
  FieldDeviceSettingDto,
  Rollout,
  RolloutCreateRequest,
  Selector,
  SelectorArgs,
  SelectorCreateRequest,
  SelectorDto,
  SoftwarePackage,
  SoftwarePackageArgs,
  SoftwarePackageDefault,
  SoftwarePackageDefaultRequest,
  SoftwarePackageLog,
  SoftwarePackageLogArgs,
  SoftwarePackageLogDto,
  SoftwarePackageVersion,
  SoftwarePackageVersionArgs,
  SoftwarePackageVersionConfig,
  SoftwarePackageVersionConfigArgs,
  SoftwarePublisher,
  TenantDeploymentRing,
  TenantDeploymentRingGetArgs,
  TenantDeploymentRingRequest,
  TenantDeploymentRingSummary,
  TenantDeploymentRingSummaryDto
} from "@libs/api/gateways/field/field-ops-gateway.dtos";
import { IFieldOpsGateway } from "@libs/api/gateways/field/field-ops-gateway.interface";
import {
  toDeploymentDetail,
  toDeviceDetail,
  toFieldDeviceAuthAction,
  toFieldDeviceAuthActionDto,
  toFieldDeviceIdentifier,
  toFieldDeviceProperty,
  toFieldDeviceSetting,
  toFieldDeviceSettingDto,
  toSelector,
  toSelectorDto
} from "@libs/api/gateways/field/field-ops-gateway.utils";
import {
  mapRolloutFromDto,
  RolloutArgsDto,
  RolloutDto
} from "@libs/api/gateways/plt/plt-gateway.dtos";
import { RefDataDto, RefDataResponse } from "@libs/api/types/common-dtos";
import { toChangeLog, toDateTime } from "@libs/common/common.utils";
import { guid } from "@libs/common/guid";
import { PagingResponse } from "@libs/paging/paging-response.type";
import { withPaging } from "@libs/paging/paging.utils";

import {
  MIN_PAGE_ITEMS,
  PAGING_PAGE_NUMBER,
  PAGING_TOTAL_COUNT,
  PAGING_TOTAL_PAGES
} from "../../types/headers.constants";

export class FieldOpsGateway implements IFieldOpsGateway {
  constructor(private api: AxiosInstance) {}

  async getDevices(
    filterArgs?: FieldDeviceFilterArgs,
    pageParam = 1
  ): Promise<PagingResponse<FieldDeviceDetails>> {
    const queryString = withPaging({
      ...filterArgs,
      page: pageParam,
      limit: MIN_PAGE_ITEMS
    });

    const response = await this.api.get(`/devices?${stringify(queryString)}`);

    const isMaxPages =
      +response.headers[PAGING_PAGE_NUMBER] >=
      +response.headers[PAGING_TOTAL_PAGES];

    const total = response.headers[PAGING_TOTAL_COUNT];

    const data: PagingResponse<FieldDeviceDetails> = {
      results:
        response.data !== "" && response.data !== undefined // Data is empty string when no results
          ? response?.data?.map(details => ({
              ...details,
              tokenExpiryUtc: toDateTime(details.tokenExpiryUtc),
              controllerCreatedDateTimeUtc: toDateTime(
                details.controllerCreatedDateTimeUtc
              ),
              controllerStartDateTimeUtc: toDateTime(
                details.controllerStartDateTimeUtc
              ),
              controllerLastObservedDateTimeUtc: toDateTime(
                details.controllerLastObservedDateTimeUtc
              ),
              recoveryAgentCreatedDateTimeUtc: toDateTime(
                details.recoveryAgentCreatedDateTimeUtc
              ),
              recoveryAgentStartDateTimeUtc: toDateTime(
                details.recoveryAgentStartDateTimeUtc
              ),
              recoveryAgentLastObservedDateTimeUtc: toDateTime(
                details.recoveryAgentLastObservedDateTimeUtc
              )
            }))
          : null,
      next: isMaxPages ? undefined : pageParam + 1,
      total: total ? +total : undefined
    };

    return data;
  }

  async getDeviceDetails(fieldDeviceId: guid): Promise<FieldDeviceDetails> {
    const url = qs.stringifyUrl({
      url: `devices/${fieldDeviceId}/details`,
      query: {
        fieldDeviceId
      }
    });

    const { data } = await this.api.get<FieldDeviceDetailsDto>(url);

    return {
      ...data,
      tokenExpiryUtc: toDateTime(data.tokenExpiryUtc),
      controllerCreatedDateTimeUtc: toDateTime(
        data.controllerCreatedDateTimeUtc
      ),
      controllerStartDateTimeUtc: toDateTime(data.controllerStartDateTimeUtc),
      controllerLastObservedDateTimeUtc: toDateTime(
        data.controllerLastObservedDateTimeUtc
      ),
      recoveryAgentCreatedDateTimeUtc: toDateTime(
        data.recoveryAgentCreatedDateTimeUtc
      ),
      recoveryAgentStartDateTimeUtc: toDateTime(
        data.recoveryAgentStartDateTimeUtc
      ),
      recoveryAgentLastObservedDateTimeUtc: toDateTime(
        data.recoveryAgentLastObservedDateTimeUtc
      )
    };
  }

  async getDevice(fieldDeviceId: guid): Promise<FieldDevice> {
    const { data } = await this.api.get<FieldDeviceDto>(
      `devices/${fieldDeviceId}`
    );

    return {
      ...data,
      issueTimeUtc: toDateTime(data.issueTimeUtc),
      notBeforeTimeUtc: toDateTime(data.notBeforeTimeUtc),
      tokenExpiryUtc: toDateTime(data.tokenExpiryUtc),
      changeLog: toChangeLog(data.changeLog)
    };
  }

  async getDeviceLogs(
    args?: SoftwarePackageLogArgs,
    pageParam = 1
  ): Promise<PagingResponse<SoftwarePackageLog>> {
    const queryString = withPaging({
      ...args,
      page: pageParam,
      limit: MIN_PAGE_ITEMS
    });

    const response = await this.api.get<SoftwarePackageLogDto[]>(
      `/deviceLog/GetByArgs?${stringify(queryString)}`
    );

    const isMaxPages =
      +response.headers[PAGING_PAGE_NUMBER] >=
      +response.headers[PAGING_TOTAL_PAGES];

    const total = response.headers[PAGING_TOTAL_COUNT];

    const data: PagingResponse<SoftwarePackageLog> = {
      results: response?.data?.map(details => ({
        ...details,
        blobLastModifiedDate: toDateTime(details.blobLastModifiedDate),
        logFileDate: toDateTime(details.logFileDate),
        blobCreationDate: toDateTime(details.blobCreationDate)
      })),
      next: isMaxPages ? undefined : pageParam + 1,
      total: total ? +total : undefined
    };

    return data;
  }

  async postDeploymentRing({
    code,
    description,
    displayName,
    isPublic
  }: DeploymentRingCreateRequest): Promise<DeploymentRing> {
    // Create a Deployment Ring
    const { data } = await this.api.post<DeploymentRing>("deploymentRings", {
      code,
      description,
      displayName,
      isPublic
    });
    return data;
  }

  async getDeploymentRings(): Promise<DeploymentRing[]> {
    // Get Deployment Rings
    const { data } = await this.api.get<DeploymentRing[]>("deploymentRings");

    return data;
  }

  async getDeploymentRing(deploymentRingId: string): Promise<DeploymentRing> {
    // Get Deployment Ring
    const { data } = await this.api.get<DeploymentRing>(
      `deploymentRings/${deploymentRingId}`
    );

    return data;
  }

  async deleteDeploymentRings(deploymentRingId: guid): Promise<void> {
    // Delete Deployment Rings
    const url = qs.stringifyUrl({
      url: `deploymentRings/${deploymentRingId}`
    });

    const { data } = await this.api.delete<void>(url);
    return data;
  }

  async updateDeploymentRings({
    code,
    description,
    displayName,
    isPublic,
    deploymentRingId,
    selectorId,
    eTag
  }: DeploymentRingUpdateRequest): Promise<DeploymentRing> {
    // Update Deployment Rings
    const url = qs.stringifyUrl({
      url: `deploymentRings/${deploymentRingId}`
    });

    const { data } = await this.api.put<DeploymentRing>(url, {
      code,
      description,
      displayName,
      isPublic,
      selectorId,
      eTag
    });
    return data;
  }

  async clearDeploymentRingBlock(deploymentRingId: string): Promise<void> {
    // Update Deployment Rings
    const url = qs.stringifyUrl({
      url: `deploymentRings/${deploymentRingId}/clearCache`
    });

    await this.api.post<void>(url, { deploymentRingId });
  }

  async getRollouts(deploymentRingId?: string): Promise<Rollout[]> {
    const rolloutArgsDto: RolloutArgsDto = {
      deploymentRingId
    };

    const { data } = await this.api.get<RolloutDto[]>(
      `/rollouts?${stringify(rolloutArgsDto)}`
    );

    return !!data ? data.map(mapRolloutFromDto) : [];
  }

  async getRollout(rolloutId?: string): Promise<Rollout> {
    const { data } = await this.api.get<RolloutDto>(`/rollouts/${rolloutId}`);
    return mapRolloutFromDto(data);
  }

  async postRollout(request: RolloutCreateRequest): Promise<Rollout> {
    // Create a Rollout
    const { data } = await this.api.post<RolloutDto>("rollouts", request);
    return mapRolloutFromDto(data);
  }

  async updateRollout(request: RolloutCreateRequest): Promise<Rollout> {
    // Update a Rollout
    const { data } = await this.api.put<RolloutDto>(
      `rollouts/${request.rolloutId}`,
      request
    );

    return mapRolloutFromDto(data);
  }

  async deleteRollout(rolloutId: guid): Promise<void> {
    // Delete Rollout
    const url = qs.stringifyUrl({
      url: `rollouts/${rolloutId}`
    });

    const { data } = await this.api.delete<void>(url);
    return data;
  }

  async getDeviceIdentifiers(
    fieldDeviceId: guid
  ): Promise<FieldDeviceIdentifier[]> {
    // Get Field Device Identifiers
    const url = qs.stringifyUrl({
      url: `identifiers/${fieldDeviceId}`
    });

    const { data } = await this.api.get<FieldDeviceIdentifierDto[]>(url);
    return data.map(toFieldDeviceIdentifier);
  }

  async getMostRecentDeviceIdentifierHistory(
    fieldDeviceId: guid
  ): Promise<FieldDeviceIdentifier[]> {
    // Get Field Device Identifiers
    const url = qs.stringifyUrl({
      url: `identifiers/${fieldDeviceId}/history/recent`
    });

    const { data } = await this.api.get<FieldDeviceIdentifierDto[]>(url);
    return data.map(toFieldDeviceIdentifier);
  }

  async getDeviceIdentifierHistory(
    fieldDeviceId: guid,
    identifierType: string
  ): Promise<FieldDeviceIdentifier[]> {
    // Get Field Device Identifier History
    const url = qs.stringifyUrl({
      url: `identifiers/${fieldDeviceId}/${identifierType}/history`
    });

    const { data } = await this.api.get<FieldDeviceIdentifierDto[]>(url);
    return data.map(toFieldDeviceIdentifier);
  }

  async getMostRecentDevicePropertyHistory(
    fieldDeviceId: guid
  ): Promise<FieldDeviceProperty[]> {
    // Get Field Device Properties
    const url = qs.stringifyUrl({
      url: `properties/${fieldDeviceId}/history/recent`
    });

    const { data } = await this.api.get<FieldDevicePropertyDto[]>(url);
    return data.map(toFieldDeviceProperty);
  }

  async getDeviceProperties(
    fieldDeviceId: guid
  ): Promise<FieldDeviceProperty[]> {
    // Get Field Device Properties
    const url = qs.stringifyUrl({
      url: `properties/${fieldDeviceId}`
    });

    const { data } = await this.api.get<FieldDevicePropertyDto[]>(url);
    return data.map(toFieldDeviceProperty);
  }

  async getDeviceClaims(fieldDeviceId: guid): Promise<FieldDeviceClaim[]> {
    // Get Field Device Claims
    const url = qs.stringifyUrl({
      url: `devices/fieldDevice/${fieldDeviceId}/claims`
    });

    const { data } = await this.api.get<FieldDeviceClaimDto[]>(url);
    return data?.map(claim => ({
      ...claim,
      changeLog: toChangeLog(claim.changeLog)
    }));
  }

  async getFieldDeviceDesiredConfig(
    fieldDeviceId: string,
    fieldConfigurationVersion?: string
  ): Promise<FieldDeviceConfiguration> {
    // Get Field Device Desired Config
    const url = qs.stringifyUrl({
      url: `devices/${fieldDeviceId}/desiredConfig`,
      query: {
        configurationVersion: `${fieldConfigurationVersion ??
          FieldConfigurationVersion.V2_0}`
      }
    });

    const { data } = await this.api.get<FieldDeviceConfiguration>(url);
    return data;
  }

  async getSoftwarePackgeCommandJson(
    softwarePackageVersionId?: string
  ): Promise<CustomRJSFSchema[]> {
    // Get Software Package Command Json
    const url = qs.stringifyUrl({
      url: `softwarePackageVersions/${softwarePackageVersionId}/commands`
    });

    const { data } = await this.api.get<CustomRJSFSchema[]>(url);
    return data;
  }

  async getDeviceConfigurationDiff(
    fieldDeviceId: string,
    fieldConfigurationVersion?: string
  ): Promise<DeviceConfigurationDiff> {
    // Get Device Configuration Diff
    const url = qs.stringifyUrl({
      url: `deviceConfigurationDiff/${fieldDeviceId}`,
      query: {
        configurationVersion: fieldConfigurationVersion
      }
    });

    const { data } = await this.api.get<DeviceConfigurationDiff>(url);
    return data;
  }

  async getSoftwarePackages(
    args?: SoftwarePackageArgs
  ): Promise<SoftwarePackage[]> {
    // Get Software Packages
    const url = qs.stringifyUrl({
      url: "softwarePackage",
      query: { ...args }
    });

    const { data } = await this.api.get<SoftwarePackage[]>(url);
    return data;
  }

  async getSelectors(args?: SelectorArgs): Promise<Selector[]> {
    // Get Selectors
    const url = qs.stringifyUrl({
      url: "selectors",
      query: { ...args }
    });

    const { data } = await this.api.get<SelectorDto[]>(url);

    return data?.map(toSelector);
  }

  async getSelector(selectorId: string): Promise<Selector> {
    const { data } = await this.api.get<SelectorDto>(`selectors/${selectorId}`);
    return toSelector(data);
  }

  async getSelectorEvaluate(selectorId: string): Promise<DeviceSummary[]> {
    const { data } = await this.api.get<DeviceSummary[]>(
      `selectors/${selectorId}/evaluate`
    );
    return data;
  }

  async postSelector(request: SelectorCreateRequest): Promise<Selector> {
    const { data } = await this.api.post<SelectorDto>("selectors", request);
    return toSelector(data);
  }

  async putSelector(selector: Selector): Promise<Selector> {
    const { data } = await this.api.put<SelectorDto>(
      `selectors/${selector.id}`,
      toSelectorDto(selector)
    );

    return toSelector(data);
  }

  async deleteSelector(selectorId: string): Promise<void> {
    await this.api.delete<void>(`selectors/${selectorId}`);
  }

  async getSoftwarePublishers(): Promise<SoftwarePublisher[]> {
    // Get Software Publishers
    const { data } = await this.api.get<SoftwarePublisher[]>(
      "/softwarePublishers"
    );

    return data;
  }

  async getSoftwarePackageVersion(
    softwarePackageVersionId: string
  ): Promise<SoftwarePackageVersion> {
    // Get Software Package Version
    const { data } = await this.api.get<SoftwarePackageVersion>(
      `/softwarePackageVersions/${softwarePackageVersionId}`
    );

    return data;
  }

  async getSoftwarePackageVersions(
    args?: SoftwarePackageVersionArgs
  ): Promise<SoftwarePackageVersion[]> {
    // Get Software Package Versions
    const queryString = withPaging({
      ...args,
      page: 1,
      limit: 200
    });

    const { data } = await this.api.get<SoftwarePackageVersion[]>(
      `/softwarePackageVersions?${stringify(queryString)}`
    );

    return data;
  }

  async getSoftwarePackageVersionsForRollout(
    rolloutId: string
  ): Promise<SoftwarePackageVersion[]> {
    // Get Software Package Versions for Rollout
    const { data } = await this.api.get<SoftwarePackageVersion[]>(
      `/softwarePackageVersions/rollout/${rolloutId}`
    );

    return data;
  }

  async getSoftwarePackageVersionConfig(
    softwarePackageVersionId: string
  ): Promise<SoftwarePackageVersionConfig[]> {
    const softwarePackageVersionConfigArgs: SoftwarePackageVersionConfigArgs = {
      softwarePackageVersionId
    };

    const { data } = await this.api.get<SoftwarePackageVersionConfig[]>(
      `/softwarePackageVersionConfig?${stringify(
        softwarePackageVersionConfigArgs
      )}`
    );

    return !!data ? data : [];
  }

  async getSoftwarePackageDefaults(): Promise<SoftwarePackageDefault[]> {
    // Get Software Package Defaults
    const url = qs.stringifyUrl({
      url: "softwarePackageDefaults"
    });

    const { data } = await this.api.get<SoftwarePackageDefault[]>(url);
    return data;
  }

  async updateSoftwarePackageDefault(
    request: SoftwarePackageDefault
  ): Promise<SoftwarePackageDefault> {
    return (
      await this.api.put<SoftwarePackageDefault>(
        `softwarePackageDefaults/${request.id}`,
        request
      )
    ).data;
  }

  async postSoftwarePackageDefault(
    request: SoftwarePackageDefaultRequest
  ): Promise<SoftwarePackageDefault> {
    const { data } = await this.api.post<SoftwarePackageDefault>(
      "softwarePackageDefaults",
      request
    );

    return data;
  }

  async regenerateSoftwarePackageDefaultConfig(): Promise<void> {
    await this.api.post("softwarePackageDefaults/regenerate");
  }

  async purgeFieldmanCDN(): Promise<void> {
    await this.api.get("CDN/purge");
  }

  async clearDeviceFieldConfigurationCache(
    fieldDeviceId: string
  ): Promise<void> {
    await this.api.post(`devices/${fieldDeviceId}/clearConfigCache`);
  }

  async clearDeviceCaches(
    fieldDeviceId: string,
    cacheType: FieldDeviceCacheType
  ): Promise<void> {
    await this.api.post(
      `devices/${fieldDeviceId}/clearCache`,
      cacheType.toString()
    );
  }

  async getFieldDeviceAuthActions(
    fieldDeviceId: string,
    showExecuted: boolean
  ): Promise<FieldDeviceAuthAction[] | undefined> {
    // Get Auth Actions
    const url = qs.stringifyUrl({
      url: "fieldDeviceAuthAction",
      query: {
        fieldDeviceId,
        isExecuted: showExecuted === false ? false : undefined
      }
    });

    const { data } = await this.api.get<FieldDeviceAuthActionDto[]>(url);

    if (!data) {
      return undefined;
    }

    return data?.map(authAction => toFieldDeviceAuthAction(authAction));
  }

  async postFieldDeviceAuthActions(
    request: FieldDeviceAuthActionRequest
  ): Promise<FieldDeviceAuthAction> {
    // Post Auth Actions
    const url = qs.stringifyUrl({
      url: "fieldDeviceAuthAction"
    });

    const { data } = await this.api.post<FieldDeviceAuthActionDto>(
      url,
      request
    );

    return toFieldDeviceAuthAction(data);
  }

  async updateFieldDeviceAuthActions(
    request: FieldDeviceAuthAction
  ): Promise<FieldDeviceAuthAction> {
    // Update Auth Action
    const { data } = await this.api.put<FieldDeviceAuthActionDto>(
      `fieldDeviceAuthAction/${request.id}`,
      toFieldDeviceAuthActionDto(request)
    );

    return toFieldDeviceAuthAction(data);
  }

  async deleteFieldDeviceAuthActions(authActionId: string): Promise<void> {
    // Delete Auth Action
    await this.api.delete<void>(`fieldDeviceAuthAction/${authActionId}`);
  }

  async getAuthActionRefData(): Promise<RefDataDto[]> {
    // Get Auth Actions
    const url = qs.stringifyUrl({
      url: "ref/AuthActionTypes"
    });

    const { data } = await this.api.get<RefDataResponse[]>(url);

    return data?.map(refData => ({
      ...refData,
      changeLog: toChangeLog(refData.changeLog)
    }));
  }

  async getSelectorDataTypeRefData(): Promise<RefDataDto[]> {
    // Get selector data types
    const url = qs.stringifyUrl({
      url: "ref/selectorRuleDataType"
    });

    const { data } = await this.api.get<RefDataResponse[]>(url);

    return data?.map(refData => ({
      ...refData,
      changeLog: toChangeLog(refData.changeLog)
    }));
  }

  async getFieldDeviceSettings(
    fieldDeviceId: string
  ): Promise<FieldDeviceSetting[]> {
    // Get Field Device Settings
    const url = qs.stringifyUrl({
      url: "fieldDeviceSettings",
      query: {
        fieldDeviceId
      }
    });

    const { data } = await this.api.get<FieldDeviceSettingDto[]>(url);

    return data?.map(toFieldDeviceSetting) ?? [];
  }

  async postFieldDeviceSetting(
    request: FieldDeviceSetting
  ): Promise<FieldDeviceSetting> {
    // Post Field Device Setting
    const url = qs.stringifyUrl({
      url: "fieldDeviceSettings"
    });

    const { data } = await this.api.post<FieldDeviceSettingDto>(
      url,
      toFieldDeviceSettingDto(request)
    );

    return toFieldDeviceSetting(data);
  }

  async updateFieldDeviceSetting(
    request: FieldDeviceSetting
  ): Promise<FieldDeviceSetting> {
    // Update Field Device Setting
    const { data } = await this.api.put<FieldDeviceSettingDto>(
      `fieldDeviceSettings/${request.id}`,
      toFieldDeviceSettingDto(request)
    );

    return toFieldDeviceSetting(data);
  }

  async getFieldDeviceSettingTypeRefData(): Promise<RefDataDto[]> {
    // Get Field Device Setting Types
    const url = qs.stringifyUrl({
      url: "ref/fieldDeviceSettingTypes"
    });

    const { data } = await this.api.get<RefDataResponse[]>(url);

    return data?.map(refData => ({
      ...refData,
      changeLog: toChangeLog(refData.changeLog)
    }));
  }

  async getDeploymentDetail(fieldDeviceId: string): Promise<DeploymentDetail> {
    // Get Field Device Deployment Details
    const { data } = await this.api.get<DeploymentDetailDto>(
      `devices/${fieldDeviceId}/deploymentDetail`
    );

    return toDeploymentDetail(data);
  }

  async getFieldDeviceDeploymentRings(
    fieldDeviceId?: string,
    deploymentRingId?: string
  ): Promise<DeploymentRingFieldDevice[]> {
    // Get Field Device Deployment Ring Details
    const url = qs.stringifyUrl({
      url: "deploymentRingFieldDevice",
      query: {
        fieldDeviceId,
        deploymentRingId
      }
    });

    const { data } = await this.api.get<DeploymentRingFieldDevice[]>(url);
    return data;
  }

  async getFieldDeviceDeploymentRingDetail(
    deploymentRingFieldDeviceId: string
  ): Promise<DeploymentRingFieldDeviceDetail> {
    // Get Field Device Deployment Ring Details
    const { data } = await this.api.get<DeploymentRingFieldDeviceDetail>(
      `deploymentRingFieldDevice/${deploymentRingFieldDeviceId}/details`
    );
    return data;
  }

  async postDeviceAdvancedSearch(
    filterArgs: DeviceAdvancedSearchArgs,
    pageParam = 1
  ): Promise<PagingResponse<DeviceDetail>> {
    const encoded = btoa(JSON.stringify(filterArgs));

    const queryString = withPaging({
      page: pageParam,
      limit: MIN_PAGE_ITEMS,
      encoded
    });

    const response = await this.api.get<DeviceDetailDto[]>(
      `/devices/advancedSearch?${stringify(queryString)}`
    );

    const isMaxPages =
      +response.headers[PAGING_PAGE_NUMBER] >=
      +response.headers[PAGING_TOTAL_PAGES];

    const total = response.headers[PAGING_TOTAL_COUNT];

    const data: PagingResponse<DeviceDetail> = {
      results: response?.data?.map(toDeviceDetail) ?? [],
      next: isMaxPages ? undefined : pageParam + 1,
      total: total ? +total : undefined
    };

    return data;
  }

  async getFieldDeviceReportedConfig(
    fieldDeviceId: string
  ): Promise<FieldDeviceReportedConfig[]> {
    // Get Field Device reported config
    const url = qs.stringifyUrl({
      url: `devices/${fieldDeviceId}/reportedConfig`
    });

    const { data } = await this.api.get<FieldDeviceReportedConfigDto[]>(url);

    return data?.map(
      config =>
        ({
          ...config,
          createdDateTimeUtc: toDateTime(config.createdDateTimeUtc),
          startDateTimeUtc: toDateTime(config.startDateTimeUtc),
          lastObservedDateTimeUtc: toDateTime(config.lastObservedDateTimeUtc)
        } as FieldDeviceReportedConfig)
    );
  }

  async postFieldDeviceDeploymentRing(
    request: AddDeploymentRingFieldDeviceRequest
  ): Promise<DeploymentRingFieldDevice> {
    // Create a link between the Field Device and a Deployment Ring
    const { data } = await this.api.post<DeploymentRingFieldDevice>(
      "deploymentRingFieldDevice",
      request
    );
    return data;
  }

  async deleteFieldDeviceDeploymentRing(
    deploymentRingFieldDeviceId: string
  ): Promise<void> {
    // Delete Field Device Deployment Ring link
    await this.api.delete<void>(
      `deploymentRingFieldDevice/${deploymentRingFieldDeviceId}`
    );
  }

  async updateFieldDeviceDeploymentRing(
    request: DeploymentRingFieldDevice
  ): Promise<DeploymentRingFieldDevice> {
    // Update the Field Device Deployment Ring
    const url = qs.stringifyUrl({
      url: `deploymentRingFieldDevice/${request.id}`
    });

    const { data } = await this.api.put<DeploymentRingFieldDevice>(
      url,
      request
    );
    return data;
  }

  async dispatchDeviceCommand(
    request: DeviceCommand
  ): Promise<CommandResponse> {
    const { data } = await this.api.post<CommandResponse>(
      "fieldGateway/command/dispatch",
      request
    );

    return data;
  }

  async postAssignTenantDeploymentRingBulkAction(
    request: BulkAssignTenantDeploymentRingRequest
  ): Promise<BulkActionResponse> {
    const { data } = await this.api.post<BulkActionResponse>(
      "bulk/AssignTenantDeploymentRings",
      request
    );

    return data;
  }

  async postAssignDeploymentRingBulkAction(
    request: BulkAssignDeploymentRingRequest
  ): Promise<BulkActionResponse> {
    const { data } = await this.api.post<BulkActionResponse>(
      "bulk/AssignDeploymentRing",
      request
    );

    return data;
  }

  async postBulkAuthAction(
    request: BulkAuthActionRequest
  ): Promise<BulkActionResponse> {
    const { data } = await this.api.post<BulkActionResponse>(
      "bulk/FieldDeviceAuthAction",
      request
    );

    return data;
  }

  async postBulkCommandDispatch(
    request: BulkCommandDispatchRequest
  ): Promise<BulkActionResponse> {
    const { data } = await this.api.post<BulkActionResponse>(
      "bulk/CommandDispatch",
      request
    );

    return data;
  }

  async getTenantFieldDevices(tenantId: string): Promise<DeviceSummary[]> {
    const { data } = await this.api.get<DeviceSummary[]>(
      `tenants/${tenantId}/devices`
    );

    return data;
  }

  async getTenantDeploymentRingByArgs(
    args?: TenantDeploymentRingGetArgs
  ): Promise<TenantDeploymentRingSummary[]> {
    const url = qs.stringifyUrl({
      url: "tenantDeploymentRing",
      query: { ...args }
    });

    const { data } = await this.api.get<TenantDeploymentRingSummaryDto[]>(url);

    return data?.map(TenantDeploymentRing => ({
      ...TenantDeploymentRing,
      changeLog: toChangeLog(TenantDeploymentRing.changeLog)
    }));
  }

  async postTenantDeploymentRing(
    request?: TenantDeploymentRingRequest
  ): Promise<TenantDeploymentRing> {
    const { data } = await this.api.post<TenantDeploymentRing>(
      "tenantDeploymentRing",
      request
    );

    return data;
  }

  async deleteTenantDeploymentRing(id: guid): Promise<void> {
    await this.api.delete<void>(`tenantDeploymentRing/${id}`);
  }

  async updateTenantDeploymentRing(
    TenantDeploymentRing: TenantDeploymentRing
  ): Promise<TenantDeploymentRing> {
    const { data } = await this.api.put<TenantDeploymentRing>(
      `tenantDeploymentRing/${TenantDeploymentRing.id}`,
      TenantDeploymentRing
    );

    return data;
  }
}
